// react
import React from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Grid,
  SwipeableDrawer,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Button,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AccessTime as AccessTimeIcon, AllInclusive as AllInclusiveIcon, CloseOutlined as CloseOutlinedIcon, Verified as VerifiedIcon } from "@mui/icons-material";

// components
import { Puller } from "components/common/Puller";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const softCard = (theme) => ({
  p: 2,
  borderRadius: 3,
  background: `linear-gradient(135deg, ${theme.palette.background.paper}F4, ${theme.palette.background.default}E8)`,
  border: `1px solid ${theme.palette.divider}40`,
  boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
  backdropFilter: "blur(6px)",
  transition: "all 0.25s ease",
  "&:hover": {
    boxShadow: "0 6px 22px rgba(0,0,0,0.20)",
    transform: "translateY(-2px)",
  },
});

const SectionHeader = ({ icon, label }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
    {icon}
    <Typography variant="subtitle1" fontWeight={700} color="text.secondary" sx={{ letterSpacing: "0.3px" }}>
      {label}
    </Typography>
  </Stack>
);

const BlockchainVerificationDialog = ({ verification, open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  // ------------------ CASE: NO VERIFICATION DATA ------------------
  if (!verification || !verification.txid) {
    const NoVerificationContent = (
      <Box sx={{ py: 2 }}>
        <Typography variant="body1">{t("verification_not_available")}</Typography>
      </Box>
    );

    return isMobile ? (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={() => {}}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "90%",
            borderTopLeftRadius: "4%",
            borderTopRightRadius: "4%",
          },
        }}
      >
        <Puller />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
            {t("certification_details")}
          </Typography>

          {NoVerificationContent}
        </Box>
      </SwipeableDrawer>
    ) : (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
            backdropFilter: "blur(10px)",
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
            transition: "all 0.3s ease",
          },
        }}
      >
        <DialogTitle>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" fontWeight={700}>
                {t("certification_details")}
              </Typography>
            </Grid>
            <Grid item>
              <Tooltip title={t("close")} placement="top">
                <IconButton onClick={onClose} color="error" edge="end">
                  <CloseOutlinedIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </DialogTitle>

        <DialogContent>{NoVerificationContent}</DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            variant="text"
            sx={{
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              py: 1,
            }}
            onClick={onClose}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  const CertificationID = (
    <Grid item xs={12}>
      <Card sx={(theme) => softCard(theme)}>
        <CardContent>
          <SectionHeader icon={<VerifiedIcon color="success" />} label={t("certification_id")} />
          <Typography variant="body2" sx={{ wordBreak: "break-all" }} fontWeight={600}>
            {verification.txid}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const Timeliness = (
    <Grid item xs={12}>
      <Card sx={(theme) => softCard(theme)}>
        <CardContent>
          <SectionHeader icon={<AccessTimeIcon color={verification.tx_timestamp ? "success" : "disabled"} />} label={t("timeliness")} />
          <Typography variant="body2">{verification.tx_timestamp ? `${t("timeliness_text_1")} ${convertTimestamp(verification.tx_timestamp)}` : t("not_available")}</Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const Consistency = (
    <Grid item xs={12}>
      <Card sx={(theme) => softCard(theme)}>
        <CardContent>
          <SectionHeader icon={<AllInclusiveIcon color={verification.check ? "success" : "error"} />} label={t("consistency")} />

          <Typography variant="body2">
            {verification.check ? `${t("hash_check_ok_1")} ${t("unaltered_m")} ${t("hash_check_ok_2")}` : `${t("hash_check_ko_1")} ${t("unaltered_m")} ${t("hash_check_ko_2")}`}
          </Typography>

          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              {t("calculated_hash_label")}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }} fontWeight={600}>
              {verification.record_hash}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {t("blockchain_hash_label")}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }} fontWeight={600}>
              {verification.hsh}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  const Content = (
    <Grid container spacing={2} sx={{ p: 1 }}>
      {CertificationID}
      {Timeliness}
      {Consistency}
    </Grid>
  );

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />

      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
          {t("certification_details")}
        </Typography>

        {Content}
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight={700}>
              {t("certification_details")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={onClose} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>{Content}</DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={onClose}
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

BlockchainVerificationDialog.propTypes = {
  verification: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BlockchainVerificationDialog;
